import { useState } from 'react'
import { request } from '@/lib/request'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export default function ApiTest() {
  const [endpoint, setEndpoint] = useState('/system/api/query/strategy')
  const [requestData, setRequestData] = useState(
    JSON.stringify({ page_size: 10, page_num: 1, query_filter: {} }, null, 2)
  )
  const [response, setResponse] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')

  // 获取环境变量信息
  const envInfo = {
    VITE_ALANT_URL: import.meta.env.VITE_ALANT_URL || '未配置',
    VITE_PROXY_URL: import.meta.env.VITE_PROXY_URL || '未配置',
    master模式: localStorage.getItem('master') || '未设置',
    token: localStorage.getItem('token') ? '已设置' : '未设置',
  }

  const handleTest = async () => {
    setLoading(true)
    setError('')
    setResponse(null)

    try {
      const data = JSON.parse(requestData)
      const resp = await request(endpoint, data)
      setResponse(resp)
    } catch (err: any) {
      setError(err.message || '请求失败')
      console.error('API测试错误:', err)
    } finally {
      setLoading(false)
    }
  }

  // 常用测试端点
  const testEndpoints = [
    { name: 'Strategy', path: '/system/api/query/strategy' },
    { name: 'JsScript', path: '/system/api/query/jsScript' },
    { name: 'APIs (77)', path: '/system/api/query/apis' },
    { name: 'APIs (bxj)', path: '/system/api/query/apisBxj' },
    { name: 'System APIs', path: '/system/api/query/system_apis' },
  ]

  return (
    <div className="h-full p-6 overflow-auto">
      <h1 className="text-2xl font-bold mb-6">API 连接测试</h1>

      <div className="grid gap-6 max-w-6xl">
        {/* 环境信息 */}
        <Card>
          <CardHeader>
            <CardTitle>环境配置</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 font-mono text-sm">
              {Object.entries(envInfo).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="font-semibold">{key}:</span>
                  <span className="text-muted-foreground">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 快速测试按钮 */}
        <Card>
          <CardHeader>
            <CardTitle>快速测试</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {testEndpoints.map((ep) => (
                <Button
                  key={ep.path}
                  variant="outline"
                  onClick={() => setEndpoint(ep.path)}
                  className={endpoint === ep.path ? 'bg-primary text-primary-foreground' : ''}
                >
                  {ep.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 请求配置 */}
        <Card>
          <CardHeader>
            <CardTitle>请求配置</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">API 端点</label>
              <Input
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder="/system/api/query/..."
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">请求数据 (JSON)</label>
              <textarea
                value={requestData}
                onChange={(e) => setRequestData(e.target.value)}
                className="w-full h-32 p-2 border rounded font-mono text-sm"
                placeholder='{"page_size": 10, "page_num": 1}'
              />
            </div>

            <Button onClick={handleTest} disabled={loading} className="w-full">
              {loading ? '测试中...' : '发送测试请求'}
            </Button>
          </CardContent>
        </Card>

        {/* 错误信息 */}
        {error && (
          <Card className="border-red-500">
            <CardHeader>
              <CardTitle className="text-red-500">错误</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm text-red-500">{error}</pre>
            </CardContent>
          </Card>
        )}

        {/* 响应结果 */}
        {response && (
          <Card>
            <CardHeader>
              <CardTitle>
                响应结果
                <span
                  className={`ml-4 text-sm ${
                    response.code === 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {response.code === 0 ? '✓ 成功' : `✗ 失败 (code: ${response.code})`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm bg-gray-100 p-4 rounded overflow-auto max-h-96">
                {JSON.stringify(response, null, 2)}
              </pre>
              {response.data?.list && (
                <div className="mt-4 text-sm text-muted-foreground">
                  找到 {response.data.total || response.data.list.length} 条记录
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 诊断建议 */}
        <Card>
          <CardHeader>
            <CardTitle>诊断建议</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <strong>如果看到连接错误:</strong>
              <ul className="list-disc list-inside mt-1 text-muted-foreground">
                <li>检查后端服务器是否正在运行</li>
                <li>确认 .env.development 中的 URL 配置正确</li>
                <li>检查网络连接和防火墙设置</li>
              </ul>
            </div>
            <div>
              <strong>如果看到 401/403 错误:</strong>
              <ul className="list-disc list-inside mt-1 text-muted-foreground">
                <li>需要先登录获取 token</li>
                <li>访问 /admin/login 进行登录</li>
              </ul>
            </div>
            <div>
              <strong>如果看到 404 错误:</strong>
              <ul className="list-disc list-inside mt-1 text-muted-foreground">
                <li>检查 API 端点路径是否正确</li>
                <li>确认后端接口是否存在</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
